import React, { useState, useEffect } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { useQueryClient } from '@tanstack/react-query';
import { Loader2, AlertTriangle } from 'lucide-react';

import { useDeleteRole } from '../../services/roles';

const DeleteRoleDialog = ({ show, onHide, role, onSuccess }) => {
  const queryClient = useQueryClient();
  const [needsForce, setNeedsForce] = useState(false);
  const [userCount, setUserCount] = useState(0);

  const deleteRole = useDeleteRole(
    (data) => {
      if (data?.conflict) {
        setUserCount(data.userCount);
        setNeedsForce(true);
        return;
      }
      toast.success('Role deleted');
      queryClient.invalidateQueries(['roles']);
      if (onSuccess) onSuccess();
      handleClose();
    },
    {
      onError: (error) => {
        toast.error(error.response?.data?.msg || 'Failed to delete role');
      },
    }
  );

  const handleClose = () => {
    setNeedsForce(false);
    setUserCount(0);
    onHide();
  };

  const handleDelete = () => {
    if (!role) return;

    deleteRole.mutate({
      roleId: role.roleId,
      force: needsForce,
    });
  };

  // Reset state when dialog opens
  useEffect(() => {
    if (show) {
      setNeedsForce(false);
      setUserCount(0);
    }
  }, [show]);

  if (!role) return null;

  return (
    <Modal show={show} onHide={handleClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>Delete Role</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {!needsForce && (
          <p className="mb-0">
            Are you sure you want to delete the role <strong>{role.roleDisplayName}</strong>?
          </p>
        )}

        {needsForce && (
          <div className="flex items-start gap-3 p-3 rounded-lg bg-amber-50 border border-amber-200">
            <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 shrink-0" />
            <p className="text-sm font-medium text-amber-900 mb-0">
              Deleting will remove this role from {userCount} {userCount === 1 ? 'user' : 'users'}.
            </p>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="light" onClick={handleClose}>
          Cancel
        </Button>
        <Button
          variant="danger"
          onClick={handleDelete}
          disabled={deleteRole.isLoading}
          className="inline-flex items-center gap-2"
        >
          {deleteRole.isLoading && <Loader2 className="h-4 w-4 animate-spin" />}
          {needsForce ? 'Delete Anyway' : 'Delete'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default DeleteRoleDialog;
